import { runListQuerySchema, type RunFilter, type RunListQuery } from '@console/types'

/**
 * Filter state lives entirely in the URL (UI spec §3) — this module is the
 * only place that translates between the two. `queryFromSearchParams`
 * never throws on a garbled URL (hand-edited, or a saved view applied after
 * its filter stopped validating): it falls back to the schema's own
 * defaults, because the URL is a user-facing surface, not a trusted API
 * boundary the way the backend's querystring is.
 */
export const PAGE_SIZE = 50

export function queryFromSearchParams(searchParams: URLSearchParams): RunListQuery {
  const pageParam = Number(searchParams.get('page') ?? '1')
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1
  const amountAboveParam = searchParams.get('amountAbove')

  const raw = {
    filter: {
      verdict: searchParams.get('verdict') ?? undefined,
      customer: searchParams.get('customer') ?? undefined,
      actionType: searchParams.get('actionType') ?? undefined,
      dateFrom: searchParams.get('dateFrom') ?? undefined,
      dateTo: searchParams.get('dateTo') ?? undefined,
      ruleFired: searchParams.get('ruleFired') ?? undefined,
      lineItemContains: searchParams.get('lineItemContains') ?? undefined,
      amountAbove: amountAboveParam !== null ? Number(amountAboveParam) : undefined,
    },
    sort: searchParams.get('sort') ?? undefined,
    sortDirection: searchParams.get('sortDirection') ?? undefined,
    offset: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  }

  const parsed = runListQuerySchema.safeParse(raw)
  return parsed.success ? parsed.data : runListQuerySchema.parse({})
}

export function pageFromOffset(offset: number): number {
  return Math.floor(offset / PAGE_SIZE) + 1
}

export function searchParamsForView(
  filter: RunFilter,
  page: number,
  sort: string,
  sortDirection: string,
): URLSearchParams {
  const params = new URLSearchParams()

  if (filter.verdict !== undefined) params.set('verdict', filter.verdict)
  if (filter.customer !== undefined && filter.customer !== '')
    params.set('customer', filter.customer)
  if (filter.actionType !== undefined && filter.actionType !== '') {
    params.set('actionType', filter.actionType)
  }
  if (filter.dateFrom !== undefined) params.set('dateFrom', filter.dateFrom)
  if (filter.dateTo !== undefined) params.set('dateTo', filter.dateTo)
  if (filter.ruleFired !== undefined) params.set('ruleFired', filter.ruleFired)
  if (filter.lineItemContains !== undefined) params.set('lineItemContains', filter.lineItemContains)
  if (filter.amountAbove !== undefined) params.set('amountAbove', String(filter.amountAbove))
  if (sort !== 'time') params.set('sort', sort)
  if (sortDirection !== 'desc') params.set('sortDirection', sortDirection)
  if (page > 1) params.set('page', String(page))

  return params
}
